import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ value, label, accent = "cyan" }) => {
  const accentMap = {
    cyan:   "border-cyan-400 text-cyan-400",
    green:  "border-emerald-400 text-emerald-400",
    yellow: "border-yellow-400 text-yellow-400",
    pink:   "border-pink-400 text-pink-400",
  };
  return (
    <div className={`bg-slate-900/60 border-l-4 ${accentMap[accent]} p-5 rounded-r-lg`}>
      <div className={`text-3xl md:text-4xl font-bold ${accentMap[accent].split(" ")[1]}`}>
        {value}
      </div>
      <div className="text-xs md:text-sm text-slate-400 mt-1 uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
};

const SectionTitle = ({ kicker, title }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3 mb-2">
      <div className="h-1 w-10 bg-cyan-400"></div>
      <div className="text-xs font-bold text-cyan-400 tracking-widest uppercase">
        {kicker}
      </div>
    </div>
    <h2 className="text-3xl md:text-4xl font-bold text-white">{title}</h2>
  </div>
);

export default function InvestorPitchPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/investor-metrics/summary`)
      .then((r) => setData(r.data))
      .catch((e) => console.error("Failed to load metrics", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-cyan-400 text-lg">Loading investor brief…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-red-400">Could not load metrics.</div>
      </div>
    );
  }

  const { live_metrics: m, traction, the_ask, use_of_funds, milestones, tech_moat, founder } = data;
  const fmtUsd = (n) => "$" + (n || 0).toLocaleString();
  const deckUrl = `${BACKEND_URL}${data.deck_url}`;
  const pdfUrl = `${BACKEND_URL}${data.deck_pdf_url}`;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              "radial-gradient(circle at 20% 20%, rgba(0,212,255,0.4), transparent 40%), radial-gradient(circle at 80% 60%, rgba(34,197,94,0.3), transparent 40%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-slate-900 border border-cyan-400/30 px-3 py-1 rounded-md text-xs font-bold text-cyan-400 tracking-widest">
              SEED ROUND · CONFIDENTIAL · 2026
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
            The Digital Operating System
            <br />
            <span className="text-cyan-400">for African Sports</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mb-10">
            AI-powered athlete discovery, federation management & live broadcasting —
            shipped, live, and backed by NOCK.
          </p>

          <div className="flex flex-wrap gap-3">
            <a
              href={deckUrl}
              className="bg-cyan-400 text-slate-950 px-6 py-3 rounded-lg font-bold hover:bg-cyan-300 transition"
              download
            >
              ↓ Download Pitch Deck (.pptx)
            </a>
            <a
              href={pdfUrl}
              className="bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-700 transition"
              download
            >
              ↓ PDF Version
            </a>
            <a
              href={`mailto:${founder.email}?subject=NextGen%20Tournaments%20-%20Investment%20Discussion`}
              className="bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-slate-700 transition"
            >
              ✉ Email {founder.name.split(" ")[0]}
            </a>
          </div>
        </div>
      </section>

      {/* ===== TRACTION ===== */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionTitle kicker="TRACTION" title="The distribution moat money can't buy." />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard value="NOCK" label="MoU signed — Olympic Committee" accent="yellow" />
          <StatCard value={`${traction.federations_onboarded}+`} label="Federations onboarded" accent="cyan" />
          <StatCard value={traction.sports_supported} label="Sports configured" accent="cyan" />
          <StatCard value={traction.rbac_levels} label="Admin RBAC levels" accent="green" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard value={m.athletes} label="Athletes registered" accent="cyan" />
          <StatCard value={m.tournaments} label="Tournaments active" accent="cyan" />
          <StatCard value={m.video_analyses} label="AI analyses processed" accent="cyan" />
          <StatCard value={traction.platform_modules_live} label="Modules shipped" accent="green" />
        </div>

        <div className="mt-6 text-cyan-400 font-semibold">
          → Every federation = a multi-year B2G contract competitors cannot replicate.
        </div>
      </section>

      {/* ===== THE ASK ===== */}
      <section className="bg-gradient-to-br from-cyan-950 to-slate-950 border-y border-cyan-400/20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <SectionTitle kicker="THE ASK" title={`Raising ${fmtUsd(the_ask.amount_usd)} for ${the_ask.equity_pct}% equity.`} />

          <div className="grid md:grid-cols-2 gap-6">
            {/* Structure card */}
            <div className="bg-slate-900/80 border border-cyan-400/30 rounded-xl p-6">
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-2">STRUCTURE</div>
              <div className="text-3xl font-bold mb-1">SAFE Note</div>
              <div className="text-sm text-slate-400 mb-6">
                Simple Agreement for Future Equity (YC-standard)
              </div>
              <div className="space-y-2">
                {[
                  ["Amount raising",     fmtUsd(the_ask.amount_usd)],
                  ["Valuation cap",      fmtUsd(the_ask.valuation_cap_usd) + " post-money"],
                  ["Discount",           the_ask.discount_pct + "% on next priced round"],
                  ["MFN clause",         the_ask.mfn_clause ? "Yes" : "No"],
                  ["Effective equity",   the_ask.equity_pct + "% (capped)"],
                  ["Board seat",         the_ask.board_seat ? "Yes" : "None — observer only"],
                  ["Pro-rata rights",    the_ask.pro_rata_rights ? "Yes (up to Series A)" : "No"],
                  ["Use of funds runway",the_ask.runway_months + " months"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-800/60 py-2 text-sm">
                    <span className="text-slate-400">{k}</span>
                    <span className="font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Use of funds card */}
            <div className="bg-slate-900/80 border border-emerald-400/30 rounded-xl p-6">
              <div className="text-xs font-bold text-emerald-400 tracking-widest mb-2">
                USE OF FUNDS — 18 MONTH RUNWAY
              </div>
              <div className="space-y-3 mt-4">
                {use_of_funds.map((u) => (
                  <div key={u.category}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{u.category}</span>
                      <span className="font-bold text-emerald-400">
                        {fmtUsd(u.amount_usd)} <span className="text-slate-500">({u.pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                      <div
                        className="h-full bg-emerald-400"
                        style={{ width: `${u.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-cyan-400 font-semibold">
            First $200K commitment locks the lead position.
          </div>
        </div>
      </section>

      {/* ===== TECH MOAT ===== */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionTitle kicker="TECHNOLOGY MOAT" title="AI no African competitor has shipped." />
        <div className="grid md:grid-cols-2 gap-4">
          {tech_moat.map((t) => (
            <div key={t} className="bg-slate-900/60 border-l-2 border-emerald-400 p-4 rounded-r-lg">
              <div className="text-sm">{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== ROADMAP ===== */}
      <section className="bg-slate-900/30 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <SectionTitle kicker="ROADMAP" title="From Kenya to Pan-Africa." />
          <div className="space-y-3">
            {milestones.map((mil, i) => (
              <div key={mil.quarter} className="flex gap-4 items-center bg-slate-900/60 rounded-lg p-4">
                <div className={`px-4 py-2 rounded font-bold text-sm min-w-[110px] text-center
                  ${i < 2 ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/30"
                  : i < 4 ? "bg-cyan-400/10 text-cyan-400 border border-cyan-400/30"
                  : "bg-yellow-400/10 text-yellow-400 border border-yellow-400/30"}`}>
                  {mil.quarter}
                </div>
                <div className="text-slate-200">{mil.goal}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionTitle kicker="LET'S TALK" title="Ready to back African sport's digital backbone?" />
        <div className="bg-slate-900/80 border-l-4 border-cyan-400 rounded-r-xl p-8">
          <div className="grid md:grid-cols-2 gap-y-4 gap-x-8">
            <div>
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-1">FOUNDER</div>
              <div className="text-xl font-bold">{founder.name}</div>
            </div>
            <div>
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-1">EMAIL</div>
              <a href={`mailto:${founder.email}`} className="text-xl hover:text-cyan-400">{founder.email}</a>
            </div>
            <div>
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-1">PHONE</div>
              <a href={`tel:${founder.phone.replace(/\s/g,"")}`} className="text-xl hover:text-cyan-400">{founder.phone}</a>
            </div>
            <div>
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-1">PLATFORM</div>
              <div className="text-sm break-all">renew-app.preview.emergentagent.com</div>
            </div>
            <div className="md:col-span-2 pt-4 border-t border-slate-800">
              <div className="text-xs font-bold text-cyan-400 tracking-widest mb-1">DEMO LOGIN (MASTER ADMIN)</div>
              <div className="text-sm font-mono">admin@nextgenafrica.com  ·  admin123</div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-500 text-xs">
          Generated live · {new Date(data.generated_at).toLocaleString()}
        </div>
      </section>
    </div>
  );
}
